<template>
  <div>
    <router-link
      class="inline-flex items-center gap-2 text-[13px] text-muted no-underline mb-6 transition-colors duration-150 hover:text-ink hover:no-underline"
      :to="{ name: 'admin-bodies' }"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
      Back to bodies
    </router-link>

    <loader v-if="isLoading"></loader>

    <template v-if="body && !isLoading">
      <!-- Header -->
      <div class="bg-white border border-black/6 rounded-[20px] p-6 mb-7 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div class="flex items-start justify-between gap-5">
          <div class="flex items-start gap-4">
            <img v-if="body.logo" class="w-14 h-14 object-contain shrink-0" :src="`/static/images/${body.logo}`" :alt="body.title" width="56">
            <div>
              <h1 class="text-2xl font-bold text-ink tracking-[-0.03em] mb-1">{{ body.title }}</h1>
              <p v-if="body.description" class="text-sm text-[#645B66] leading-normal m-0">{{ body.description }}</p>
            </div>
          </div>
          <a
            href="#"
            @click.prevent="openEditModal(body)"
            class="btn-outline"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            Edit
          </a>
        </div>
      </div>

      <!-- Talent requirements -->
      <div v-if="body.requirements && exceptionalTalentRequirements.length" class="mb-7">
        <div class="flex items-center gap-2.5 mb-3">
          <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Exceptional Talent requirements</span>
          <span class="bg-[#f0eef5] text-primary text-xs font-semibold px-2.5 py-0.5 rounded-full">{{ exceptionalTalentRequirements.length }}</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            class="group flex items-center justify-between bg-white border border-black/6 rounded-[20px] px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-[box-shadow,border-color] duration-200 hover:border-black/8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.07)]"
            v-for="(requirement, index) in exceptionalTalentRequirements"
            :key="'t-' + index"
          >
            <div class="flex flex-col gap-0.5 min-w-0">
              <a href="#" class="text-sm font-medium text-ink no-underline hover:text-primary hover:no-underline" @click.prevent="openRequirementModal(requirement, index)">{{ requirement.title }}</a>
              <span class="text-xs text-muted">{{ getCategoryTitle(requirement.category) }}</span>
            </div>
            <a
              href="#"
              class="edit-icon opacity-40 group-hover:opacity-100"
              @click.prevent="openRequirementModal(requirement, index)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </a>
          </div>
        </div>
      </div>

      <!-- Promise requirements -->
      <div v-if="body.requirements && exceptionalPromiseRequirements.length" class="mb-7">
        <div class="flex items-center gap-2.5 mb-3">
          <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Exceptional Promise requirements</span>
          <span class="bg-[#f0eef5] text-primary text-xs font-semibold px-2.5 py-0.5 rounded-full">{{ exceptionalPromiseRequirements.length }}</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            class="group flex items-center justify-between bg-white border border-black/6 rounded-[20px] px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-[box-shadow,border-color] duration-200 hover:border-black/8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.07)]"
            v-for="(requirement, index) in exceptionalPromiseRequirements"
            :key="'p-' + index"
          >
            <div class="flex flex-col gap-0.5 min-w-0">
              <a href="#" class="text-sm font-medium text-ink no-underline hover:text-primary hover:no-underline" @click.prevent="openRequirementModal(requirement, index)">{{ requirement.title }}</a>
              <span class="text-xs text-muted">{{ getCategoryTitle(requirement.category) }}</span>
            </div>
            <a
              href="#"
              class="edit-icon opacity-40 group-hover:opacity-100"
              @click.prevent="openRequirementModal(requirement, index)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </a>
          </div>
        </div>
      </div>

      <!-- Components -->
      <div v-if="bodyCategories && bodyCategories.length > 0">
        <div class="mb-7" v-for="(component, cIndex) in bodyCategories" :key="cIndex">
          <div class="flex items-center gap-2.5 mb-3">
            <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">{{ component.title }}</span>
            <a
              href="#"
              class="edit-icon opacity-100"
              @click.prevent="openEditBodyClassModal(component)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </a>
          </div>
          <div class="grid grid-cols-3 max-md:grid-cols-1 gap-2">
            <div
              class="group flex items-center justify-between bg-white border border-black/6 rounded-[20px] px-3.5 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-[box-shadow,border-color] duration-200 hover:border-black/8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.07)]"
              v-for="(week, wIndex) in filteredComponents(component.slug)"
              :key="wIndex"
            >
              <span class="text-sm font-medium text-ink">{{ week.title }}</span>
              <a
                href="#"
                class="text-muted no-underline opacity-40 transition-[opacity,color] duration-150 group-hover:opacity-100 hover:text-ink"
                @click.prevent="openEditBodyClassWeekModal(week, component, wIndex)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              </a>
            </div>
            <a
              href="#"
              class="flex items-center justify-center border border-dashed border-black/12 rounded-[20px] px-3.5 py-3 text-[13px] font-medium text-muted no-underline transition-[border-color,color] duration-150 hover:border-primary hover:text-primary hover:no-underline"
              @click.prevent="openEditBodyClassWeekModal(false, component)"
            >+ Add component</a>
          </div>
        </div>
      </div>

      <div class="text-center pt-3 pb-6">
        <a
          href="#"
          @click.prevent="openRequirementModal()"
          class="btn-dark"
        >+ Add requirement</a>
      </div>
    </template>

    <requirement-add ref="requirementModal" :body="body" :bodySlug="id" :item="requirement" :requirementIndex="requirementIndex" @update="update"></requirement-add>
    <body-add ref="modal" :item="editBody" @update="update"></body-add>
    <category-add ref="bodyClassModal" :bodySlug="id" :item="editBodyClass" @update="update"></category-add>
    <subject-class-week-add ref="bodyClassWeekModal" :bodyClass="editBodyClass" :bodySlug="id" :item="editBodyClassWeek" @update="update" :componentIndex="editComponentIndex"></subject-class-week-add>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import BodyAdd from './Includes/BodyAddModal'
import CategoryAddModal from './Includes/CategoryAddModal'
import ClassWeekAddModal from './Includes/ComponentAddModal'
import RequirementAddModal from './Includes/RequirementAddModal'
import { mapState } from 'vuex'

export default {
  name: 'admin-subject',
  props: ['id'],
  components: {
    bodyAdd: BodyAdd,
    categoryAdd: CategoryAddModal,
    subjectClassWeekAdd: ClassWeekAddModal,
    requirementAdd: RequirementAddModal,
    draggable
  },
  data () {
    return {
      editBody: null,
      editVariant: null,
      editQuestion: null,
      editQuestionIndex: null,
      editBodyClass: null,
      editBodyClassWeek: null,
      editComponentIndex: null,
      requirement: null,
      requirementIndex: null,
      isLoading: false
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      body: state => state.managers.body,
      bodyCategories: state => state.components.bodyCategories,
      bodyComponents: state => state.components.bodyComponents,
      applicationTypes: state => state.auth.applicationTypes,
      requirementTypes: state => state.auth.requirementTypes
    }),
    exceptionalTalentRequirements () {
      return this.body.requirements.filter(requirement => requirement.type === 'talent')
    },
    exceptionalPromiseRequirements () {
      return this.body.requirements.filter(requirement => requirement.type === 'promise')
    }
  },
  methods: {
    openEditModal (item) {
      this.editBody = item || null
      this.$nextTick(() => {
        this.$refs.modal.showModal()
      })
    },
    filteredComponents (category) {
      return this.bodyComponents.filter((item) => {
        return item.category === category
      })
    },
    getCategoryTitle (category) {
      const item = this.requirementTypes.find((item) => {
        return item.value === category
      })
      return item ? item.text : ''
    },
    getApplicationTitle (type) {
      const item = this.applicationTypes.find((item) => {
        return item.value === type
      })
      return item ? item.text : ''
    },
    update () {
      this.$store.dispatch('GET_BODY', this.id)
      this.$store.dispatch('GET_BODY_CATEGORIES', this.id)
      this.$store.dispatch('GET_SINGLE_BODY_COMPONENTS', this.id)
      this.$refs.modal.hideModal()
    },
    openRequirementModal (item, index) {
      this.requirement = item || null
      this.requirementIndex = index != null ? index : null
      this.$nextTick(() => {
        this.$refs.requirementModal.showModal()
      })
    },
    closeRequirementModal () {
      this.$refs.requirementModal.hideModal()
    },
    openEditBodyClassModal (item) {
      this.editBodyClass = item || null
      this.$nextTick(() => {
        this.$refs.bodyClassModal.showModal()
      })
    },
    openEditBodyClassWeekModal (item, subjectClass, index) {
      this.editComponentIndex = index
      this.editBodyClass = subjectClass
      this.editBodyClassWeek = item || null
      this.$nextTick(() => {
        this.$refs.bodyClassWeekModal.showModal()
      })
    }
  },
  mounted () {
  },
  created () {
    if (this.user && !this.user.isAdmin) {
      this.$router.push({ name: 'admin-applicants' })
      return
    }
    this.isLoading = true
    this.$store.dispatch('GET_BODY', this.id).then(() => {
      this.isLoading = false
    })
    this.$store.dispatch('GET_BODY_CATEGORIES', this.id)
    this.$store.dispatch('GET_SINGLE_BODY_COMPONENTS', this.id)
  }
}
</script>

<style scoped>
.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #131415;
  background: #fff;
  border: 1px solid #d6d8e3;
  border-radius: 9999px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.btn-outline:hover {
  border-color: #c2c5d0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  text-decoration: none;
  color: #131415;
}

.btn-dark {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: #131415;
  border: 1px solid #131415;
  border-radius: 9999px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.btn-dark:hover {
  opacity: 0.85;
  color: #fff;
  text-decoration: none;
}

.edit-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #c0bcc4;
  text-decoration: none;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, opacity 0.15s;
}
.edit-icon:hover {
  background: #f4f5fa;
  color: #131415;
}
</style>
